import { useCallback, useEffect, useRef, useState } from 'react'
import { cn, showToast, Tooltip } from '@quickmd-llc/design-system'
import { getErrorObject } from '@quickmd-llc/frontend-utils'
import { addSentryBreadcrumb } from 'utils/sentryUtility'

// How long the tooltip label stays swapped to the "copied" state after a click.
const COPIED_RESET_MS = 1500

type Props = {
  /** The exact string written to the clipboard on click. */
  value: string
  /** Tooltip label shown on hover before a copy (e.g. "Copy number"). */
  copyLabel: string
  /** Tooltip label shown briefly after a successful copy. */
  copiedLabel?: string
  /** Accessible name for the copy control — include the value so it's announced. */
  ariaLabel: string
  className?: string
  children: React.ReactNode
}

/**
 * A hoverable field that copies `value` to the clipboard on click and swaps its
 * tooltip label to "Copied". Wrap the icon + text of a patient detail (phone, MRN)
 * so the doctor can copy it without triggering the surrounding row's click handler
 * — the click is stopped from propagating.
 */
export const CopyableField = ({ value, copyLabel, copiedLabel = 'Copied', ariaLabel, className, children }: Props) => {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
    },
    [],
  )

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      if (resetTimer.current) clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS)
    } catch (error: unknown) {
      const err = getErrorObject(error)
      addSentryBreadcrumb({
        category: 'copy-to-clipboard',
        message: `Failed to copy field: ${err.message}`,
        level: 'warning',
      })
      showToast({ type: 'error', message: "Couldn't copy to clipboard." })
    }
  }, [value])

  return (
    <Tooltip content={copied ? copiedLabel : copyLabel}>
      <div
        role='button'
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation()
          void handleCopy()
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          e.preventDefault()
          e.stopPropagation()
          void handleCopy()
        }}
        className={cn('group/copy rounded-sp-0.5 inline-flex cursor-pointer items-center gap-sp-0.25', className)}>
        {children}
      </div>
    </Tooltip>
  )
}
